import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PaginationHeader from "../../components/pagination/PaginationHeader";
import PaginationLayout from "../../components/pagination/PaginationLayout";
import { UserRole } from "../../utils/api/endpoints/user/credentials";
import usePrograms from "../../utils/hooks/programs/usePrograms";
import useUserCredentials from "../../utils/hooks/user/useUserCredentials";
import ProgramCard from "../../components/programs/cards/ProgramCard";
import { isProgramDraft } from "../../utils/programs/statusUtils";
import {
  CTBProgram,
  ProgramType
} from "../../utils/api/endpoints/programs/parsePrograms";
import ProgramQuickView from "../../components/programs/ProgramQuickView";
import ProgramsRightSidebar from "../../components/programs/cards/ProgramsRightSidebar";
import { useRef } from "react";
import usePageTitle from "../../utils/hooks/usePageTitle";
import FilterDropdown from "../../components/filters/inputs/FilterDropdown";
import FilterToolBar from "../../components/filters/FilterToolBar";

const DEFAULT_PAGE_COUNT = 10;

const PaginatedPrograms = () => {
  const [selectedProgram, setSelectedProgram] = useState<CTBProgram>();
  useEffect(() => {
    const listener = (e: any) => {
      const program = e?.detail;
      if (!program) return;
      const needRedispatch = !selectedProgram || selectedProgram.id !== program.id;
      setSelectedProgram(program);
      if (needRedispatch) {
        // Re-dispatch only when the sidebar is being newly mounted for this program
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent("program:open-stats", { detail: program }));
        }, 0);
      }
    };
    window.addEventListener("program:open-stats", listener as any);
    return () => window.removeEventListener("program:open-stats", listener as any);
  }, [selectedProgram]);

  // Ensure sidebar mounts and then re-dispatch open-info so it can scroll
  useEffect(() => {
    const listener = (e: any) => {
      const program = e?.detail;
      if (!program) return;
      const needRedispatch = !selectedProgram || selectedProgram.id !== program.id;
      setSelectedProgram(program);
      if (needRedispatch) {
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent("program:open-info", { detail: program }));
        }, 0);
      }
    };
    window.addEventListener("program:open-info", listener as any);
    return () => window.removeEventListener("program:open-info", listener as any);
  }, [selectedProgram]);

  // Ensure sidebar mounts and then show recent updates without scroll
  useEffect(() => {
    const listener = (e: any) => {
      const program = e?.detail;
      if (!program) return;
      const needRedispatch = !selectedProgram || selectedProgram.id !== program.id;
      setSelectedProgram(program);
      if (needRedispatch) {
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent("program:open-updates", { detail: program }));
        }, 0);
      }
    };
    window.addEventListener("program:open-updates", listener as any);
    return () => window.removeEventListener("program:open-updates", listener as any);
  }, [selectedProgram]);
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState<number>(0);
  const { programs, count, filters, isLoading, isError, setFilters, setPage } =
    usePrograms();
  const { role } = useUserCredentials();
  const navigate = useNavigate();

  usePageTitle("Programs");

  const isAdminRole = role
    ? [
      UserRole.ADMIN,
      UserRole.ADMIN_MANAGER,
      UserRole.SUB_ADMIN
    ].includes(role)
    : false;

  // Redirect admins to company programs page
  useEffect(() => {
    if (isAdminRole) {
      navigate("/dashboard/companies", { replace: true });
    }
  }, [isAdminRole, navigate]);
  // measure header position so the sidebar starts just below it
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;

    const recompute = () => {
      const rect = el.getBoundingClientRect();
      // distance from header top to bottom + small margin
      const offsetWithinHeader = rect.height + 8; // 8px gap below header
      setHeaderHeight(offsetWithinHeader);
    };

    const resizeObserver = new ResizeObserver(recompute);
    resizeObserver.observe(el);
    recompute();

    window.addEventListener("resize", recompute);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", recompute);
    };
  }, []);

  // Show all valid programs including drafts (with Draft status badge)
  const visiblePrograms = programs.filter(program => program !== null && program !== undefined);
  const adjustedCount = count;

  return (
    <div className="relative flex h-full flex-col" style={{ backgroundColor: "#f9fafb" }}>
      <div ref={headerRef}>
        <PaginationHeader
          title="Programs"
          button={
            role === UserRole.BUSINESS ||
              role === UserRole.BUSINESS_MANAGER ||
              role === UserRole.BUSINESS_ADMINISTRATOR ||
              role === UserRole.ADMIN ||
              role === UserRole.SUB_ADMIN ||
              role === UserRole.ADMIN_MANAGER
              ? {
                  label: "Create a Program",
                  href: (() => {
                    const activeCompanyId = sessionStorage.getItem("ctb_admin_active_company_id");
                    if (isAdminRole && activeCompanyId) {
                      return `/dashboard/create-program?company_id=${activeCompanyId}`;
                    }
                    return "/dashboard/create-program";
                  })()
                }
              : undefined
          }
        >
          <FilterToolBar
            filters={filters}
            onChange={filters => setFilters(filters)}
            layout="toolbar"
            className="mt-4" // Equal left/right handled by page padding
          >
            <div className="w-full">
              <FilterDropdown
                value={(filters.type as string) || ""}
                options={["", ...Object.values(ProgramType)]}
                placeholderText="Any Type"
                onChange={value =>
                  setFilters({
                    ...filters,
                    type: value === "" ? undefined : (value as ProgramType)
                  })
                }
              />
            </div>

            <div className="w-full">
              <FilterDropdown
                value={(filters.private?.[0] as string) || ""}
                options={["", "Public", "Private"]}
                placeholderText="Any Access"
                onChange={value =>
                  setFilters({
                    ...filters,
                    private:
                      value === "" ? undefined : [value as "Public" | "Private"]
                  })
                }
              />
            </div>


            {role === UserRole.ADMIN && (
              <div className="w-full">
                <FilterDropdown
                  value={(filters.activated?.[0] as string) || ""}
                  options={["", "Active", "Inactive"]}
                  placeholderText="Any Activation"
                  onChange={value =>
                    setFilters({
                      ...filters,
                      activated:
                        value === ""
                          ? undefined
                          : [value as "Active" | "Inactive"]
                    })
                  }
                />
              </div>
            )}
          </FilterToolBar>
        </PaginationHeader>
      </div>

      <PaginationLayout
        page={filters.page || 0}
        setPage={setPage}
        count={filters.limit || DEFAULT_PAGE_COUNT}
        totalItems={adjustedCount}
        className="h-full overflow-auto"
      >
        {isLoading ? (
          <p className="col-span-2 text-center">Loading...</p>
        ) : isError ? (
          <p className="col-span-2 text-center">Failed to load programs...</p>
        ) : visiblePrograms.length > 0 ? (
          visiblePrograms.map(
            (program, index) => (
              <ProgramCard
                key={index}
                program={program}
                onClick={() => {
                  if (selectedProgram === program) {
                    setSelectedProgram(undefined);
                  } else {
                    setSelectedProgram(program);
                  }
                }}
              />
            )
          )
        ) : (
          <p className="col-span-2 text-center">No programs found...</p>
        )}
      </PaginationLayout>

      {/* New right sidebar for program quick view */}
      <ProgramsRightSidebar
        program={selectedProgram}
        topOffset={headerHeight}
        onClose={() => setSelectedProgram(undefined)}
      />

    </div>
  );
};

export default PaginatedPrograms;
